'use client';

import React, { FC, useEffect, useState } from 'react';
import styles from './SignIn.module.scss';
import { AuthCard } from '@/modules/auth/components/AuthCard';
import { Banner, BannerProps } from '@/components/atoms/Banner';
import { Button, ButtonLink, ButtonVariants } from '@/components/atoms/Button';
import { Typographies, Typography } from '@/components/atoms/Typography';
import { TextField } from '@/components/molecules/TextField';
import { PasswordField } from '@/components/molecules/PasswordField';
import { revalidateLogic, useForm } from '@tanstack/react-form';
import { ROUTES } from '@/routes/constants';
import { emailValidation } from '@/modules/auth/utils/validation';
import Link from 'next/link';
import { COMPANY_EMAIL } from '@/constants/common';
import { useSignIn } from '../../queries';
import { useAuthErrorMessages } from '../../hooks/useAuthErrorMessages';
import SignInImg from '@/assets/images/sign-in.webp';
import { extractErrorMessage } from '@/utils/error';
import { useSearchParams } from 'next/navigation';
import { Trans, useTranslation } from 'react-i18next';

const SignIn: FC = () => {
  const { t } = useTranslation();

  const [bannerMessage, setBannerMessage] = useState<BannerProps | null>(null);

  const searchParams = useSearchParams();
  const { mutateAsync: signIn, reset } = useSignIn();
  const getAuthErrorMessage = useAuthErrorMessages();

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    onSubmit: async ({ value }) => {
      try {
        reset();
        setBannerMessage(null);

        await signIn(value);
      } catch (error: unknown) {
        const errorMessage = extractErrorMessage(error);
        setBannerMessage(getAuthErrorMessage(errorMessage));
      }
    },
  });

  useEffect(() => {
    const redirectCase = searchParams.get('redirect_case');

    if (redirectCase && redirectCase === 'reset_password') {
      setBannerMessage({
        title: t('Your password has been reset successfully'),
        text: '',
        type: 'success',
      });
      const newUrl = window.location.pathname;
      window.history.replaceState(null, '', newUrl);
    }
  }, [searchParams, t]);

  return (
    <AuthCard
      title={t('Welcome back!')}
      quote={
        <Trans i18nKey="sign_in.quote" components={{ br: <br /> }}>
          As you start walk on the <br /> way, the way appears
        </Trans>
      }
      img={SignInImg}
      message={
        bannerMessage ? (
          <Banner type={bannerMessage.type} title={bannerMessage.title} text={bannerMessage.text} />
        ) : null
      }
      FormComponent={() => (
        <div>
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="email"
              validators={{
                onDynamic: ({ value }) => emailValidation(value, t),
              }}
            >
              {(field) => (
                <TextField
                  label={t('Email')}
                  placeholder={t('Enter your email')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length ? field.state.meta.errors[0] : ''}
                />
              )}
            </form.Field>

            <form.Field
              name="password"
              validators={{
                onChange: ({ value }) => {
                  if (!value) {
                    return 'Password is required';
                  }
                  return undefined;
                },
              }}
            >
              {(field) => {
                return (
                  <PasswordField
                    label={t('Password')}
                    placeholder={t('Enter your password')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                );
              }}
            </form.Field>

            <Link href={ROUTES.ForgotPassword} className={styles.link}>
              <Typography variant={Typographies.BODY_2_REGULAR}>{t('Forgot password?')}</Typography>
            </Link>

            <form.Subscribe selector={(state) => state}>
              {(state) => (
                <Button
                  variant={ButtonVariants.ACCENT}
                  type="submit"
                  size="big"
                  isFullWidth
                  disabled={!(state.isTouched && state.isValid && Object.values(state.values).every(Boolean))}
                  isLoading={state.isSubmitting}
                >
                  {t('Sign In')}
                </Button>
              )}
            </form.Subscribe>
          </form>

          <Typography variant={Typographies.CAPTION_REGULAR} className={styles.support}>
            {t('For assistance, please contact our support team at')}{' '}
            <Link href={`mailto:${COMPANY_EMAIL}`}>
              <span>{COMPANY_EMAIL}</span>
            </Link>
          </Typography>
        </div>
      )}
      FooterComponent={() => (
        <div className={styles.footer}>
          <Typography variant={Typographies.BODY_1_REGULAR}>{t("Don't have an account?")}</Typography>
          <ButtonLink variant={ButtonVariants.SECONDARY} href={ROUTES.SignUp}>
            {t('Sign Up')}
          </ButtonLink>
        </div>
      )}
    />
  );
};

export { SignIn };
